import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, SegmentedControl, Skeleton } from "../components/ui";
import { deleteScan, fetchScanDetail, fetchScanResults, reprocessScan } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import type { PantryScanImage, PantryScanSubmission } from "../types";

type ScanTab = "summary" | "items" | "insights";

interface ScanResultItem {
  id: string;
  name: string;
  category: string | null;
  quantity: string | null;
  confidence: number | null;
  sourceImageKey: string | null;
}

interface ScanCategoryCount {
  category: string;
  count: number;
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function toStringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toNumberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    const single = toStringValue(value);
    return single ? [single] : [];
  }
  return value.map(toStringValue).filter((entry): entry is string => Boolean(entry));
}

function formatTimestamp(value?: string | null): string {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").trim().toUpperCase() || "UNKNOWN";
}

function looksLikeOpaqueLabel(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  const normalized = trimmed.toLowerCase();
  if (/^[a-z0-9_-]{10,}$/.test(normalized) && !normalized.includes(" ")) {
    return true;
  }
  if (/^(scan|submission|result)\s*[#:_-]?\s*[a-z0-9_-]{3,}$/i.test(trimmed)) {
    return true;
  }
  return false;
}

function friendlyScanTitle(scan: PantryScanSubmission, routeScanId: string): string {
  const rawTitle = (scan.title ?? "").trim();
  if (!rawTitle) {
    return "Kitchen shelf scan";
  }
  const normalized = rawTitle.toLowerCase();
  const routeId = routeScanId.trim().toLowerCase();
  if (routeId && (normalized === routeId || normalized.includes(routeId) || normalized.includes(routeId.slice(0, 8)))) {
    return "Kitchen shelf scan";
  }
  if (looksLikeOpaqueLabel(rawTitle)) {
    return "Kitchen shelf scan";
  }
  return rawTitle;
}

function extractResultItems(payload: unknown): ScanResultItem[] {
  const root = asObject(payload);
  const itemsValue = root?.items;
  if (!Array.isArray(itemsValue)) {
    return [];
  }

  const normalized: ScanResultItem[] = [];
  for (const [index, item] of itemsValue.entries()) {
    const entry = asObject(item);
    if (!entry) {
      continue;
    }
    const id =
      toStringValue(entry.id) ??
      toStringValue(entry.item_id) ??
      toStringValue(entry.itemId) ??
      `scan-item-${index + 1}`;
    const name =
      toStringValue(entry.item_name) ??
      toStringValue(entry.itemName) ??
      toStringValue(entry.name) ??
      toStringValue(entry.normalized_name) ??
      "Unnamed item";
    const quantityRaw = toNumberValue(entry.quantity) ?? toStringValue(entry.quantity);
    normalized.push({
      id,
      name,
      category: toStringValue(entry.category),
      quantity: quantityRaw === null ? null : String(quantityRaw),
      confidence: toNumberValue(entry.confidence),
      sourceImageKey: toStringValue(entry.source_image_key) ?? toStringValue(entry.sourceImageKey),
    });
  }
  return normalized;
}

function extractCategoryCounts(items: ScanResultItem[]): ScanCategoryCount[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const label = item.category || "Uncategorized";
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((left, right) => right.count - left.count);
}

function extractConfidenceBands(items: ScanResultItem[]): { high: number; medium: number; low: number; unknown: number } {
  let high = 0;
  let medium = 0;
  let low = 0;
  let unknown = 0;
  for (const item of items) {
    if (item.confidence === null) {
      unknown += 1;
      continue;
    }
    if (item.confidence >= 0.8) {
      high += 1;
      continue;
    }
    if (item.confidence >= 0.55) {
      medium += 1;
      continue;
    }
    low += 1;
  }
  return { high, medium, low, unknown };
}

function extractResultNotes(payload: unknown): string[] {
  const root = asObject(payload);
  const nextSteps = asObject(root?.next_steps);
  const notes = [
    ...toStringArray(root?.notes),
    ...toStringArray(root?.recommendations),
    ...toStringArray(root?.suggestions),
    ...toStringArray(root?.warnings),
    ...toStringArray(nextSteps?.quick_wins),
    ...toStringArray(nextSteps?.actions),
  ];
  return Array.from(new Set(notes));
}

function extractResultMetadata(payload: unknown): Array<{ label: string; value: string }> {
  const root = asObject(payload);
  const meta = asObject(root?.meta);
  const fields: Array<{ label: string; value: string | null }> = [
    { label: "Result status", value: toStringValue(root?.status) },
    { label: "Model version", value: toStringValue(meta?.version) ?? toStringValue(root?.model) },
    {
      label: "Generated",
      value: toStringValue(meta?.generated_at) ?? toStringValue(meta?.generatedAt) ?? toStringValue(root?.generated_at),
    },
  ];
  return fields
    .filter((field): field is { label: string; value: string } => Boolean(field.value))
    .map((field) => ({
      label: field.label,
      value: field.label === "Generated" ? formatTimestamp(field.value) : field.value,
    }));
}

function resolveImageSource(image: PantryScanImage): string | null {
  return image.presigned_url ?? image.url ?? null;
}

function ScanImageTile({ image, index }: { image: PantryScanImage; index: number }) {
  const source = resolveImageSource(image);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const fallbackImage = assetUrl("pantry-inventory-bg.png");
  const canRenderImage = Boolean(source && !hasError && !isTimedOut);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setIsTimedOut(false);
    if (!source) {
      return;
    }
    const timeoutHandle = window.setTimeout(() => {
      setIsTimedOut(true);
    }, 5500);
    return () => window.clearTimeout(timeoutHandle);
  }, [source]);

  return (
    <article className="overflow-hidden rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
      <div className="relative h-52 w-full">
        <img src={fallbackImage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        {canRenderImage ? (
          <>
            <img
              src={source as string}
              alt={`Pantry scan photo ${index + 1}`}
              className={`absolute inset-0 h-full w-full object-cover transition ${isLoaded ? "opacity-100" : "opacity-0"}`}
              onLoad={() => {
                setIsLoaded(true);
                setIsTimedOut(false);
              }}
              onError={() => {
                setHasError(true);
                setIsLoaded(false);
                setIsTimedOut(false);
              }}
            />
            {!isLoaded ? (
              <div className="absolute inset-0 flex items-end justify-start bg-gradient-to-b from-black/20 via-black/15 to-black/50 p-3">
                <span className="rounded-full bg-white/90 px-2 py-1 text-xs font-semibold text-[var(--pr-text-muted)]">Loading photo...</span>
              </div>
            ) : null}
          </>
        ) : (
          <div className="relative flex h-full items-end justify-start overflow-hidden p-3">
            <p className="relative rounded-full bg-white/90 px-2 py-1 text-xs font-semibold text-[var(--pr-text-muted)]">
              Preview unavailable
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
        <span className="truncate text-[var(--pr-text-muted)]">Photo {index + 1}</span>
        {source ? (
          <a href={source} target="_blank" rel="noreferrer" className="font-semibold text-[var(--pr-accent-deep)]">
            Open image
          </a>
        ) : null}
      </div>
    </article>
  );
}

export function ScanDetailPage() {
  const { scanId = "" } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<PantryScanSubmission | null>(null);
  const [results, setResults] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const [tab, setTab] = useState<ScanTab>("summary");

  async function load(): Promise<void> {
    if (!scanId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [scanPayload, resultsPayload] = await Promise.all([fetchScanDetail(scanId), fetchScanResults(scanId)]);
      setScan(scanPayload);
      setResults(resultsPayload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load scan detail.");
      setScan(null);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [scanId]);

  async function handleReprocess(): Promise<void> {
    if (!scanId) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      const updated = await reprocessScan(scanId);
      setScan(updated);
      const updatedResults = await fetchScanResults(scanId);
      setResults(updatedResults);
      trackEvent("scan_reprocessed", { source: "scan_detail" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reprocess scan.");
    } finally {
      setPending(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!scanId) {
      return;
    }
    const confirmed = window.confirm("Delete this scan and all linked items?");
    if (!confirmed) {
      return;
    }
    setPending(true);
    try {
      await deleteScan(scanId);
      trackEvent("scan_deleted", { source: "scan_detail" });
      navigate("/app/scans");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete scan.");
      setPending(false);
    }
  }

  const resultItems = useMemo(() => extractResultItems(results), [results]);
  const categoryCounts = useMemo(() => extractCategoryCounts(resultItems), [resultItems]);
  const confidence = useMemo(() => extractConfidenceBands(resultItems), [resultItems]);
  const resultNotes = useMemo(() => extractResultNotes(results), [results]);
  const resultMetadata = useMemo(() => extractResultMetadata(results), [results]);
  const scanImages = scan?.images ?? [];
  const readyImageCount = scanImages.filter((image) => Boolean(resolveImageSource(image))).length;
  const unresolvedImageCount = Math.max(scanImages.length - readyImageCount, 0);

  return (
    <div data-testid="scan-detail-page" className="space-y-4 pb-4">
      <PageHeader
        title="Pantry scan"
        subtitle="Review submitted photos and extracted pantry items."
        right={
          <Link to="/app/scans" className="text-sm font-semibold text-[var(--pr-accent-deep)]">
            Back to scans
          </Link>
        }
      />

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
          <Skeleton className="h-72" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : !scan ? (
        <ErrorState message="Scan not found." onRetry={() => void load()} />
      ) : (
        <>
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">{friendlyScanTitle(scan, scanId)}</h2>
                <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Created {formatTimestamp(scan.created_at)}</p>
                <p className="text-sm text-[var(--pr-text-muted)]">Updated {formatTimestamp(scan.updated_at)}</p>
              </div>
              <Pill tone="accent" label={statusLabel(scan.status)} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => void handleReprocess()} disabled={pending}>
                Reprocess
              </Button>
              <Button variant="ghost" className="border border-[var(--pr-border)] text-rose-600" onClick={() => void handleDelete()} disabled={pending}>
                Delete
              </Button>
              <Button variant="ghost" onClick={() => void load()} disabled={pending}>
                Refresh
              </Button>
            </div>
          </Card>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Submitted photos</h3>
                <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
                  {scanImages.length} photo{scanImages.length === 1 ? "" : "s"} linked to this scan.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Pill label={`${readyImageCount} ready`} />
                {unresolvedImageCount > 0 ? <Pill label={`${unresolvedImageCount} unavailable`} /> : null}
              </div>
            </div>

            {scanImages.length === 0 ? (
              <EmptyState
                title="No photos available"
                subtitle="This scan does not currently expose image previews."
                image={assetUrl("pantry-inventory-bg.png")}
              />
            ) : (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {scanImages.map((image, index) => (
                  <ScanImageTile key={image.id} image={image} index={index} />
                ))}
              </div>
            )}
          </Card>

          <Card>
            <SegmentedControl
              value={tab}
              options={[
                { value: "summary", label: "Summary" },
                { value: "items", label: "Pantry items" },
                { value: "insights", label: "Insights" },
              ]}
              onChange={(value) => {
                const selectedTab = value as ScanTab;
                setTab(selectedTab);
                trackEvent("scan_detail_tab_selected", { tab: selectedTab });
              }}
            />

            {tab === "summary" ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Card flat>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Detected items</p>
                  <p className="pr-display mt-2 text-3xl font-semibold">{resultItems.length}</p>
                  <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Extracted from latest analysis payload.</p>
                </Card>
                <Card flat>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Last processed</p>
                  <p className="mt-2 text-base font-semibold">{formatTimestamp(scan.processed_at ?? scan.updated_at)}</p>
                  <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Status updates after each reprocess.</p>
                </Card>
              </div>
            ) : null}

            {tab === "items" ? (
              resultItems.length === 0 ? (
                <EmptyState title="No extracted pantry items" subtitle="Reprocess this scan after confirming your upload photos." />
              ) : (
                <div className="mt-4 space-y-2">
                  {resultItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-2 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{item.name}</p>
                        <p className="text-xs text-[var(--pr-text-muted)]">{item.category || "Uncategorized"}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {item.quantity ? <Pill label={`Qty ${item.quantity}`} /> : null}
                        {item.confidence !== null ? <Pill tone="accent" label={`${Math.round(item.confidence * 100)}% confidence`} /> : null}
                        {item.sourceImageKey ? <Pill label="Image-linked" /> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : null}

            {tab === "insights" ? (
              categoryCounts.length === 0 && resultNotes.length === 0 && resultMetadata.length === 0 ? (
                <EmptyState
                  title="No insights available yet"
                  subtitle="Reprocess this scan after upload to generate a richer analysis view."
                />
              ) : (
                <div className="mt-4 space-y-3">
                  <div className="grid gap-3 lg:grid-cols-2">
                    <Card flat>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Confidence bands</p>
                      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                        <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">High</p>
                          <p className="mt-1 text-lg font-semibold">{confidence.high}</p>
                        </div>
                        <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Medium</p>
                          <p className="mt-1 text-lg font-semibold">{confidence.medium}</p>
                        </div>
                        <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Low</p>
                          <p className="mt-1 text-lg font-semibold">{confidence.low}</p>
                        </div>
                        <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Unknown</p>
                          <p className="mt-1 text-lg font-semibold">{confidence.unknown}</p>
                        </div>
                      </div>
                    </Card>

                    <Card flat>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Category coverage</p>
                      {categoryCounts.length === 0 ? (
                        <p className="mt-3 text-sm text-[var(--pr-text-muted)]">No category tags were returned in this analysis.</p>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {categoryCounts.map((entry) => (
                            <Pill key={entry.category} label={`${entry.category} (${entry.count})`} />
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>

                  {resultNotes.length > 0 ? (
                    <Card flat>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Guidance notes</p>
                      <ul className="mt-3 space-y-2 text-sm">
                        {resultNotes.map((note) => (
                          <li key={note} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                            {note}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ) : null}

                  {resultMetadata.length > 0 ? (
                    <Card flat>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Processing context</p>
                      <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                        {resultMetadata.map((entry) => (
                          <div key={entry.label} className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2">
                            <dt className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">{entry.label}</dt>
                            <dd className="mt-1 text-sm font-semibold">{entry.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </Card>
                  ) : null}
                </div>
              )
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}
